import { get } from "lodash";

import { StateAbbreviation, STATES_BY_ABBREVIATION } from "@stw/states";
import { TargetingForm, TargetingFormFilter } from "@stw/yup-validators";

import { formatDollarsFromCents } from "./formatDollars";
import { plural } from "./plural";

const operationReadableNameMap: Record<string, string> = {
  recurringDonationAmount: "recurring donation amount",
  lastDonation: "last donation amount",
  numberOfDonations: "number of donations value",
  averageDonation: "average donation amount",
  highestDonation: "highest donation amount",
} as const;

export function generateTargetGroupSentence(
  filters: TargetingFormFilter[],
  prefix: string = "subscribers who"
): string {
  const conditions = filters.reduce(
    (acc: string[], filter: TargetingFormFilter) => {
      switch (filter.filterOperation) {
        case "isInState":
        case "notInState":
        case "isInCustomState":
        case "notInCustomState": {
          if (filter.values && filter.values.length > 0) {
            const prefixMap = {
              isInState: "live in",
              notInState: "do not live in",
              isInCustomState: `have a ${filter.fieldName} value of`,
              notInCustomState: `do not have a ${filter.fieldName} value of`,
            };
            const valueString = filter.values
              .map(
                s =>
                  STATES_BY_ABBREVIATION[s as StateAbbreviation]?.name || null
              )
              .filter(v => !!v)
              .join(" or ");
            const prefix = prefixMap[filter.filterOperation];
            acc.push(`${prefix} ${valueString}`);
          }
          break;
        }
        case "isInZip":
        case "notInZip":
        case "isInCustomZip":
        case "notInCustomZip": {
          if (filter.values && filter.values.length > 0) {
            const prefixMap = {
              isInZip: "live in",
              notInZip: "do not live in",
              isInCustomZip: `have a ${filter.fieldName} value of`,
              notInCustomZip: `do not have a ${filter.fieldName} value of`,
            };
            const valueString = filter.values.join(" or ");
            const prefix = prefixMap[filter.filterOperation];
            acc.push(`${prefix} ${valueString}`);
          }
          break;
        }
        case "isInZipRadius":
        case "notInZipRadius":
        case "isInCustomZipRadius":
        case "notInCustomZipRadius": {
          if (
            filter.values &&
            filter.values.length > 0 &&
            filter.filterConfig?.centerZips &&
            filter.filterConfig.radius
          ) {
            const prefixMap = {
              isInZipRadius: "live within",
              notInZipRadius: "do not live within",
              isInCustomZipRadius: `have a ${filter.fieldName} value within`,
              notInCustomZipRadius: `do not have a ${filter.fieldName} value within`,
            };
            const zipString = filter.filterConfig?.centerZips.join(" or ");
            const distanceString = plural(
              Number(filter.filterConfig.radius),
              "mile",
              "miles"
            );
            const prefix = prefixMap[filter.filterOperation];
            acc.push(`${prefix} ${distanceString} of ${zipString}`);
          }
          break;
        }
        case "isInAreaCode":
        case "notInAreaCode": {
          if (filter.values && filter.values.length > 0) {
            const prefix = `have area code ${filter.filterOperation === "isInAreaCode" ? "" : "not "}equal to`;
            const valueString = filter.values.join(" or ");
            acc.push(`${prefix} ${valueString}`);
          }
          break;
        }

        case "totalDonationGt": {
          if (filter.value) {
            acc.push(
              `have total contributions greater than ${formatDollarsFromCents(Number(filter.value))}`
            );
          }
          break;
        }
        case "totalDonationLt": {
          if (filter.value) {
            acc.push(
              `have total contributions less than ${formatDollarsFromCents(Number(filter.value))}`
            );
          }
          break;
        }
        case "totalDonationBetween": {
          if (
            filter.values &&
            filter.values.length === 2 &&
            filter.values.every(v => !Number.isNaN(Number(v)))
          ) {
            const [low, high] = filter.values;
            acc.push(
              `have total contributions between ${formatDollarsFromCents(Number(low))} and ${formatDollarsFromCents(Number(high))}`
            );
          }
          break;
        }
        case "isRecurringDonor":
        case "notRecurringDonor": {
          acc.push(
            `are ${filter.filterOperation === "notRecurringDonor" ? "not " : ""}recurring donors`
          );
          break;
        }
        case "contains":
        case "notContains": {
          if (filter.value) {
            acc.push(
              `have a ${filter.fieldName} value that ${filter.filterOperation === "notContains" ? "does not contain" : "contains"} ${filter.value}`
            );
          }
          break;
        }
        case "matches": {
          if (filter.value) {
            acc.push(
              `have a ${filter.fieldName} value that matches ${filter.value}`
            );
          }
          break;
        }
        case "equals":
        case "notEquals": {
          if (filter.value) {
            acc.push(
              `have a ${filter.fieldName} value ${filter.filterOperation === "notEquals" ? "that does not equal" : "equal"} to ${filter.value}`
            );
          }
          break;
        }
        case "between":
        case "notBetween": {
          if (
            filter.values &&
            filter.values.length === 2 &&
            filter.values.every(v => !Number.isNaN(Number(v)))
          ) {
            const [low, high] = filter.values;
            acc.push(
              `have a ${filter.fieldName} value that is ${filter.filterOperation === "between" ? "" : "not "}between ${Number(low).toLocaleString()} and ${Number(high).toLocaleString()}`
            );
          }
          break;
        }
        case "customTrue":
        case "customFalse": {
          acc.push(
            `have a ${filter.fieldName} value of ${filter.filterOperation === "customTrue" ? "true" : "false"}`
          );
          break;
        }
        case "oneOf":
        case "notOneOf": {
          if (filter.values && filter.values.length > 0) {
            const valueString = filter.values.join(" or ");
            acc.push(
              `have a ${filter.fieldName} value that is ${filter.filterOperation === "oneOf" ? "" : "not "}${valueString}`
            );
          }
          break;
        }
        case "customIsNull":
        case "isNull": {
          if (filter.fieldName) {
            acc.push(`have a ${filter.fieldName} value of empty`);
          }
          break;
        }
        case "zipIsNull":
        case "stateIsNull": {
          const fieldName = filter.filterOperation.replace("IsNull", "");
          acc.push(`have an empty ${fieldName.toLowerCase()} value`);

          break;
        }
        case "gt":
        case "lt": {
          if (filter.value) {
            if (Number.isNaN(Number(filter.value))) {
              break;
            }
            acc.push(
              `have a ${filter.fieldName} value ${filter.filterOperation === "gt" ? "greater than" : "less than"} ${Number(filter.value).toLocaleString()}`
            );
          }
          break;
        }
        case "recurringDonationAmountGt":
        case "recurringDonationAmountLt":
        case "lastDonationGt":
        case "lastDonationLt":
        case "averageDonationGt":
        case "averageDonationLt":
        case "highestDonationGt":
        case "highestDonationLt": {
          const operationName = filter.filterOperation.replace(/(Gt|Lt)$/, "");
          const operationType = filter.filterOperation.endsWith("Gt")
            ? "gt"
            : "lt";

          const readableName = operationReadableNameMap[operationName];

          if (filter.value) {
            acc.push(
              `have a ${readableName} ${operationType === "gt" ? "greater than" : "less than"} ${formatDollarsFromCents(
                Number(filter.value)
              )}`
            );
          }
          break;
        }
        case "numberOfDonationsGt":
        case "numberOfDonationsLt": {
          const operationName = filter.filterOperation.replace(/(Gt|Lt)$/, "");
          const operationType = filter.filterOperation.endsWith("Gt")
            ? "gt"
            : "lt";

          const readableName = operationReadableNameMap[operationName];

          if (filter.value) {
            acc.push(
              `have a ${readableName} ${operationType === "gt" ? "greater than" : "less than"} ${Number(filter.value).toLocaleString()}`
            );
          }
          break;
        }
        case "recurringDonationAmountBetween":
        case "lastDonationBetween":
        case "averageDonationBetween":
        case "highestDonationBetween": {
          if (filter.values && filter.values.length === 2) {
            const [low, high] = filter.values;
            if (Number.isNaN(Number(low)) || Number.isNaN(Number(high))) {
              break;
            }
            const operationName = filter.filterOperation.replace("Between", "");
            const readableName = operationReadableNameMap[operationName];

            acc.push(
              `have a ${readableName} between ${formatDollarsFromCents(
                Number(low)
              )} and ${formatDollarsFromCents(Number(high))}`
            );
          }
          break;
        }
        case "numberOfDonationsBetween": {
          if (filter.values && filter.values.length === 2) {
            const [low, high] = filter.values;
            if (Number.isNaN(Number(low)) || Number.isNaN(Number(high))) {
              break;
            }

            const operationName = filter.filterOperation.replace("Between", "");
            const readableName = operationReadableNameMap[operationName];

            acc.push(
              `have a ${readableName} between ${Number(
                low
              ).toLocaleString()} and ${Number(high).toLocaleString()}`
            );
          }
          break;
        }
        case "hasDonated":
        case "notHasDonated": {
          acc.push(
            `${filter.filterOperation === "notHasDonated" ? "have not" : "have"} donated before`
          );
          break;
        }
        case "hasMobileDonation":
        case "notHasMobileDonation": {
          acc.push(
            ` ${filter.filterOperation === "notHasMobileDonation" ? "have not" : "have"} made a donation on a mobile device`
          );
          break;
        }
        case "isInSavedSearch": {
          const targetGroups = get(
            filter,
            "filterConfig.searchBody.targetGroups",
            []
          ) as TargetingForm["targetGroups"];

          const paragraph = targetGroups
            .map(tg => generateTargetGroupSentence(tg.clauses))
            .join(" OR ");
          acc.push(paragraph);
        }
      }
      return acc;
    },
    [] as string[]
  );

  return conditions.length > 0 ? `${prefix} ${conditions.join(" AND ")}` : "";
}
